import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useCallbackRef } from './use-callback-ref.js';

// https://github.com/jaredLunde/react-hook/blob/master/packages/throttle/README.md
const perf = typeof performance !== 'undefined' ? performance : Date;
const now = () => perf.now();

/**
 * A hook that invokes its callback at most X frames per second.
 * A trailing call is guaranteed, but you may opt-in to calling on the leading edge, as well.
 * @param callback This is the callback you want to throttle. You need to wrap closures/unstable callbacks in useCallback() so that they are stable, otherwise throttling will break between renders.
 * @param fps Defines the rate in frames per second with which setState is invoked with new state
 * @param leading Calls setState on the leading edge (right away). When false, setState will not be called until the next frame is due
 * @returns A throttled version of your callback
 */
export function useThrottleCallback<T extends (...args) => unknown>(
  callback: T | undefined,
  fps = 30,
  leading = false
): T {
  const storedCallback = useCallbackRef(callback);
  const ms = 1000 / fps;
  const prev = useRef(0);
  const trailingTimeout = useRef<ReturnType<typeof setTimeout>>();
  const clearTrailing = () =>
    trailingTimeout.current && clearTimeout(trailingTimeout.current);

  // Reset any time the deps change
  useEffect(
    () => () => {
      prev.current = 0;
      clearTrailing();
    },
    [fps, leading, storedCallback]
  );

  return useCallback(
    ((...args) => {
      const rightNow = now();
      const call = () => {
        prev.current = rightNow;
        clearTrailing();
        storedCallback(...args);
      };
      const current = prev.current;
      // leading
      if (leading && current === 0) return call();
      // body
      if (rightNow - current > ms) {
        if (current > 0) return call();
        prev.current = rightNow;
      }
      // trailing
      clearTrailing();
      trailingTimeout.current = setTimeout(() => {
        call();
        prev.current = 0;
      }, ms);
    }) as T,
    [leading, ms, storedCallback]
  );
}
/**
 * A hook that acts just like React.useState, but with a setState function that is only invoked at most X frames per second.
 * A trailing call is guaranteed, but you may opt-in to calling on the leading edge, as well.
 * @param initialState The initial state provided to React.useState()
 * @param fps 30	Defines the rate in frames per second with which setState is invoked with new state
 * @param leading Calls setState on the leading edge (right away). When false, setState will not be called until the next frame is due
 * @returns [state, setState]
 */
export function useThrottle<State>(
  initialState: State | (() => State),
  fps?: number,
  leading?: boolean
): [State, Dispatch<SetStateAction<State>>] {
  const state = useState<State>(initialState);
  return [state[0], useThrottleCallback(state[1], fps, leading)];
}
